/**
 * @file VTNrf54LDPI.ts
 * @author Rapoo Device SDK Team
 * @date 2026-01-05
 * @description VT Nrf54L 系列鼠标 DPI 功能实现
 *              负责处理 DPI 档位的读取、写入、切换以及启用状态管理
 *              核心逻辑：基于 EEPROM 地址读写 (Read/Write Memory)
 * @dependencies BaseDevice, DPIFeature, CommandQueue
 */

import { DPIFeature } from '../../../../features/dpi/contract'
import { GearType, DPIDirect } from '../../../../types/dpi'
import { ADDRESS_VT_CH584 } from '../../protocols/address'
import { v4 as uuidv4 } from 'uuid'
import { CommonAddress } from '../../../../types/protocol'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'

/**
 * VT Nrf54L 系列设备的 DPI 功能实现类
 * 继承自 DPIFeature 基类
 */
export class VTCH584DPI extends DPIFeature {
  /**
   * 辅助：给地址增加偏移量
   */
  private addAddressOffset(address: CommonAddress, offset: number): CommonAddress {
    const val = address[0] + (address[1] << 8) + offset
    return [val & 0xff, (val >> 8) & 0xff]
  }

  // --- 接口实现 ---

  /**
   * 获取所有 DPI 档位配置
   *
   * @param configIndex - 配置文件索引 (默认为 0)
   * @returns DPI 档位列表
   */
  async getGears(configIndex: number = 0): Promise<GearType[]> {
    const ADDRESS_X = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_X_LIST, configIndex)
    const ADDRESS_Y = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_Y_LIST, configIndex)
    const MAX_GEAR_LENGTH = ADDRESS_VT_CH584.MOUSE_DPI_GEAR_LENGTH
    const Length = MAX_GEAR_LENGTH * 2

    const parseData = (data: DataView) => {
      // 原项目逻辑：从第4个字节开始，每2个字节是一个 uint16
      // data layout: [ACK, ?, ?, ?, D0_L, D0_H, D1_L, D1_H, ...]
      const res: number[] = []
      let index = 4
      // 遍历 7 个档位
      for (let i = 0; i < MAX_GEAR_LENGTH; i++) {
        if (index + 1 >= data.byteLength) break
        const val = data.getUint16(index, true) // Little Endian
        res.push(val)
        index += 2
      }
      return res
    }

    // 并行读取 X 和 Y 轴数据
    // 注册 dpi:gears tag 用于 setDeviceDPI 等局部写入时使整体档位缓存失效
    const gearTag = `dpi:gears:${configIndex}`
    const [xValues, yValues] = await Promise.all([
      sendRead(this.device, ADDRESS_X, Length, parseData, [gearTag]),
      sendRead(this.device, ADDRESS_Y, Length, parseData, [gearTag]),
    ])

    // 读取可用档位数量 (用于设置 enable)
    let enableCount: number = MAX_GEAR_LENGTH
    try {
      enableCount = await this.getEnableGear(configIndex)
    } catch (_error) {
      // ignore error, default to max
    }

    // 组合成 GearType
    const gears: GearType[] = []
    for (let i = 0; i < MAX_GEAR_LENGTH; i++) {
      gears.push({
        id: uuidv4(),
        x: xValues[i] || 0,
        y: yValues[i] || 0,
        independentXY: !(xValues[i] === yValues[i]),
        enable: i < enableCount + 1,
      })
    }

    return gears
  }

  /**
   * 设置所有 DPI 档位配置
   *
   * @param gears - 档位列表
   * @param configIndex - 配置文件索引
   */
  async setDeviceGears(gears: GearType[], configIndex: number = 0): Promise<void> {
    const ADDRESS_X = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_X_LIST, configIndex)
    const ADDRESS_Y = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_Y_LIST, configIndex)
    const MAX_GEAR_LENGTH = ADDRESS_VT_CH584.MOUSE_DPI_GEAR_LENGTH

    // 构造 X 和 Y 的数据包 (uint16 little endian)
    const dataX: number[] = []
    const dataY: number[] = []

    // 确保不超过最大档位数
    const count = Math.min(gears.length, MAX_GEAR_LENGTH)

    for (let i = 0; i < count; i++) {
      const gear = gears[i]
      // Split X into low/high byte
      dataX.push(gear.x & 0xff)
      dataX.push((gear.x >> 8) & 0xff)

      // Split Y into low/high byte
      dataY.push(gear.y & 0xff)
      dataY.push((gear.y >> 8) & 0xff)
    }

    // 串行写入 (CommandQueue 会保证顺序)
    await sendWrite(this.device, ADDRESS_X, dataX)
    await sendWrite(this.device, ADDRESS_Y, dataY)

    // 更新可用档位数量 (假设 gears 长度即为 enabled 数量，或者根据 enable 字段统计)
    // 这里简单取 gears.length 或统计 enable=true 的数量
    const enableCount = gears.filter((g) => g.enable).length
    // 写入 enable count? 接口没有定义 setEnableGear，但通常 setDeviceGears 应该处理这个
    // 我们尝试写入 ENABLE_GEAR 地址
    try {
      const ADDRESS_ENABLE = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_ENABLE_GEAR, configIndex)
      await sendWrite(this.device, ADDRESS_ENABLE, [enableCount - 1])
    } catch (e) {
      console.warn('Failed to set enable gear count', e)
    }
  }

  /**
   * 获取当前 DPI 索引
   * @param configIndex - 配置文件索引，默认为 0
   * @returns 当前生效的 DPI 档位索引 (0-based)
   */
  async getCurrentGear(configIndex: number = 0): Promise<number> {
    const ADDRESS = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_CUR, configIndex)
    return sendRead(
      this.device,
      ADDRESS,
      1,
      (data) => {
        return data.getUint8(4)
      },
      // dpi:current 供物理 DPI 按钮上报路径失效(tag-mapping §1 场景B)
      [`dpi:current:${configIndex}`],
    )
  }

  /**
   * 设置当前 DPI 档位
   * @param index - 目标档位索引
   * @param configIndex - 配置文件索引，默认为 0
   */
  async setDeviceGear(index: number, configIndex: number = 0): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_CUR, configIndex)
    await sendWrite(this.device, ADDRESS, [index])
  }

  /**
   * 获取可用档位数量
   */
  async getEnableGear(configIndex: number = 0): Promise<number> {
    const ADDRESS = getAddress(ADDRESS_VT_CH584.MOUSE_DPI_ENABLE_GEAR, configIndex)
    return sendRead(this.device, ADDRESS, 1, (data) => {
      return data.getUint8(4)
    })
  }

  /**
   * 设置特定档位的 DPI 值
   */
  async setDeviceDPI(
    index: number,
    value: number,
    direct: DPIDirect,
    configIndex: number = 0,
  ): Promise<void> {
    const baseAddress =
      direct === 'x'
        ? getAddress(ADDRESS_VT_CH584.MOUSE_DPI_X_LIST, configIndex)
        : getAddress(ADDRESS_VT_CH584.MOUSE_DPI_Y_LIST, configIndex)

    // 每个档位 2 字节
    const offset = index * 2
    const targetAddress = this.addAddressOffset(baseAddress, offset)

    const data = [value & 0xff, (value >> 8) & 0xff]
    await sendWrite(this.device, targetAddress, data)

    // sendWrite 内部调用 invalidateByAddressRange 只失效了 2 字节的写入区间，
    // 但 getGears 缓存的 key 是整个 MOUSE_DPI_X/Y_LIST 的 14 字节区间，
    // 对于 index > 0 的情况，偏移后的地址区间与缓存区间不重叠，缓存无法自动失效。
    // 因此需要按 dpi:gears tag 手动失效整个档位列表的缓存。
    this.device.cache.invalidateByTag(`dpi:gears:${configIndex}`)
  }
}
